/**
 * Dialog for editing an active user's account role and team memberships
 * Uses tabs to organize account role and team assignments
 */
import { Check, ChevronDown, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";

interface EditUserDialogProps {
	user: { id: string; name: string | null; email: string };
	onClose: () => void;
	onSuccess: () => void;
}

/**
 * Dialog for editing a user's account role and team memberships
 * @param root0 - Component props
 * @param root0.user - The user to edit
 * @param root0.onClose - Callback when dialog is closed
 * @param root0.onSuccess - Callback when user is successfully updated
 */
export function EditUserDialog({
	user,
	onClose,
	onSuccess,
}: EditUserDialogProps) {
	const [activeTab, setActiveTab] = useState<"account" | "teams">("account");

	// Account role state
	const [selectedAccountRoleId, setSelectedAccountRoleId] = useState<string | null>(null);
	const [accountRoleSelectorOpen, setAccountRoleSelectorOpen] = useState(false);

	// Team membership state - track pending changes
	const [teamChanges, setTeamChanges] = useState<{
		add: { teamId: string; roleId: string }[];
		remove: string[];
		update: { teamId: string; roleId: string }[];
	}>({ add: [], remove: [], update: [] });

	// Fetch user's current data
	const userQuery = trpc.permissionGroups.getAccountUser.useQuery({ userId: user.id });

	// Fetch all roles for the account
	const rolesQuery = trpc.roles.list.useQuery();

	// Fetch all teams for the account
	const teamsQuery = trpc.permissionGroups.listAccountTeams.useQuery();

	const utils = trpc.useUtils();

	// Set initial account role when data loads
	useEffect(() => {
		if (userQuery.data) {
			setSelectedAccountRoleId(userQuery.data.accountRole.id);
		}
	}, [userQuery.data]);

	// Update account role mutation
	const setAccountRoleMutation = trpc.permissionGroups.setAccountUserRole.useMutation({
		onSuccess: () => {
			onSuccess();
		},
	});

	// Team mutations
	const addTeamMemberMutation = trpc.teams.addMember.useMutation();
	const setTeamRoleMutation = trpc.teams.setMemberRole.useMutation();
	const removeTeamMemberMutation = trpc.teams.removeMember.useMutation();

	/**
	 * Saves account role changes
	 */
	const handleSaveAccountRole = () => {
		if (!selectedAccountRoleId || selectedAccountRoleId === userQuery.data?.accountRole.id) {
			return;
		}

		setAccountRoleMutation.mutate({
			userId: user.id,
			roleId: selectedAccountRoleId,
		});
	};

	/**
	 * Applies all pending team changes
	 */
	const handleSaveTeamChanges = async () => {
		const promises: Promise<unknown>[] = [];

		// Process removes first
		for (const teamId of teamChanges.remove) {
			promises.push(
				removeTeamMemberMutation.mutateAsync({
					teamId,
					userId: user.id,
				})
			);
		}

		// Process updates
		for (const change of teamChanges.update) {
			promises.push(
				setTeamRoleMutation.mutateAsync({
					teamId: change.teamId,
					userId: user.id,
					roleId: change.roleId,
				})
			);
		}

		// Process adds last
		for (const change of teamChanges.add) {
			promises.push(
				addTeamMemberMutation.mutateAsync({
					teamId: change.teamId,
					userId: user.id,
					roleId: change.roleId,
				})
			);
		}

		await Promise.all(promises);

		// Queries default to a 5-minute staleTime, so without an explicit
		// invalidation the reopened dialog and user list keep serving cached
		// team data and the membership change looks like it failed.
		void utils.permissionGroups.getAccountUser.invalidate({ userId: user.id });
		void utils.permissionGroups.listAccountUsers.invalidate();

		// Reset changes and trigger success
		setTeamChanges({ add: [], remove: [], update: [] });
		onSuccess();
	};

	/**
	 * Gets the effective team memberships including pending changes
	 */
	const getEffectiveTeams = () => {
		if (!userQuery.data) return [];

		// Start with current teams
		let teams = userQuery.data.teams.map((t) => ({
			id: t.id,
			name: t.name,
			roleId: t.role.id,
			roleName: t.role.name,
			isNew: false,
		}));

		// Remove deleted teams
		teams = teams.filter((t) => !teamChanges.remove.includes(t.id));

		// Apply role updates
		teams = teams.map((t) => {
			const update = teamChanges.update.find((u) => u.teamId === t.id);
			if (update) {
				const role = rolesQuery.data?.find((r) => r.id === update.roleId);
				return {
					...t,
					roleId: update.roleId,
					roleName: role?.name ?? t.roleName,
				};
			}
			return t;
		});

		// Add new teams
		for (const add of teamChanges.add) {
			const team = teamsQuery.data?.find((t) => t.id === add.teamId);
			const role = rolesQuery.data?.find((r) => r.id === add.roleId);
			if (team && role) {
				teams.push({
					id: team.id,
					name: team.name,
					roleId: role.id,
					roleName: role.name,
					isNew: true,
				});
			}
		}

		return teams;
	};

	/**
	 * Adds a new team membership
	 * @param teamId - The ID of the team to add
	 * @param roleId - The ID of the role for the team membership
	 */
	const handleAddTeam = (teamId: string, roleId: string) => {
		setTeamChanges((prev) => ({
			...prev,
			add: [...prev.add, { teamId, roleId }],
		}));
	};

	/**
	 * Removes a team membership
	 * @param teamId - The ID of the team to remove
	 */
	const handleRemoveTeam = (teamId: string) => {
		// Check if it's a pending add - just remove from add list
		const pendingAdd = teamChanges.add.find((a) => a.teamId === teamId);
		if (pendingAdd) {
			setTeamChanges((prev) => ({
				...prev,
				add: prev.add.filter((a) => a.teamId !== teamId),
			}));
			return;
		}

		// Add to remove list
		setTeamChanges((prev) => ({
			...prev,
			remove: [...prev.remove, teamId],
			// Also clear any pending updates for this team
			update: prev.update.filter((u) => u.teamId !== teamId),
		}));
	};

	/**
	 * Updates a team membership's role
	 * @param teamId - The ID of the team to update
	 * @param roleId - The new role ID for the team membership
	 */
	const handleUpdateTeamRole = (teamId: string, roleId: string) => {
		// Check if it's a pending add - update the add entry
		const pendingAddIndex = teamChanges.add.findIndex((a) => a.teamId === teamId);
		if (pendingAddIndex >= 0) {
			setTeamChanges((prev) => ({
				...prev,
				add: prev.add.map((a, i) =>
					i === pendingAddIndex ? { ...a, roleId } : a
				),
			}));
			return;
		}

		// Update existing team
		setTeamChanges((prev) => {
			// Remove any existing update for this team
			const filteredUpdates = prev.update.filter((u) => u.teamId !== teamId);

			// Check if this reverts to original role
			const originalTeam = userQuery.data?.teams.find((t) => t.id === teamId);
			if (originalTeam?.role.id === roleId) {
				return { ...prev, update: filteredUpdates };
			}

			return {
				...prev,
				update: [...filteredUpdates, { teamId, roleId }],
			};
		});
	};

	const effectiveTeams = getEffectiveTeams();
	const availableTeams = teamsQuery.data?.filter(
		(t) => !effectiveTeams.some((et) => et.id === t.id)
	) ?? [];

	const selectedAccountRole = rolesQuery.data?.find((r) => r.id === selectedAccountRoleId);
	const hasAccountRoleChanges = selectedAccountRoleId !== userQuery.data?.accountRole.id;
	const hasTeamChanges = teamChanges.add.length > 0 ||
		teamChanges.remove.length > 0 ||
		teamChanges.update.length > 0;

	const isLoading = userQuery.isLoading || rolesQuery.isLoading || teamsQuery.isLoading;
	const isSaving = setAccountRoleMutation.isPending ||
		addTeamMemberMutation.isPending ||
		setTeamRoleMutation.isPending ||
		removeTeamMemberMutation.isPending;

	return (
		<Dialog open onOpenChange={onClose}>
			<DialogContent className="sm:max-w-[600px]">
				<DialogHeader>
					<DialogTitle>Edit {user.name ?? user.email}</DialogTitle>
					<DialogDescription>
						Manage this user&apos;s account role and team memberships.
					</DialogDescription>
				</DialogHeader>

				{isLoading ? (
					<div className="flex items-center justify-center py-8">
						<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
					</div>
				) : (
					<Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as "account" | "teams"); }}>
						<TabsList className="grid w-full grid-cols-2">
							<TabsTrigger value="account">Account Role</TabsTrigger>
							<TabsTrigger value="teams">
								Teams ({effectiveTeams.length})
							</TabsTrigger>
						</TabsList>

						{/* Account Role Tab */}
						<TabsContent value="account" className="mt-4">
							<form onSubmit={(e) => { e.preventDefault(); handleSaveAccountRole(); }} className="space-y-4">
								<div className="space-y-2">
									<Label>Account Role</Label>
									<Popover open={accountRoleSelectorOpen} onOpenChange={setAccountRoleSelectorOpen}>
										<PopoverTrigger asChild>
											<Button type="button" variant="outline" className="w-full justify-between">
												{selectedAccountRole?.name ?? "Select role..."}
												<ChevronDown className="ml-2 h-4 w-4 opacity-50" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-[450px] p-0" align="start">
											<Command>
												<CommandInput placeholder="Search roles..." />
												<CommandList>
													<CommandEmpty>No roles found.</CommandEmpty>
													<CommandGroup>
														{rolesQuery.data?.map((role) => (
															<CommandItem
																key={role.id}
																onSelect={() => {
																	setSelectedAccountRoleId(role.id);
																	setAccountRoleSelectorOpen(false);
																}}
															>
																<Check
																	className={cn(
																		"mr-2 h-4 w-4",
																		selectedAccountRoleId === role.id
																			? "opacity-100"
																			: "opacity-0"
																	)}
																/>
																<div className="flex flex-col">
																	<span>{role.name}</span>
																	{role.description && (
																		<span className="text-xs text-muted-foreground">
																			{role.description}
																		</span>
																	)}
																</div>
																{role.isSystem && (
																	<Badge variant="outline" className="ml-auto text-xs">
																		System
																	</Badge>
																)}
															</CommandItem>
														))}
													</CommandGroup>
												</CommandList>
											</Command>
										</PopoverContent>
									</Popover>
									<p className="text-xs text-muted-foreground">
										Account role determines base permissions. Team roles provide additional access within specific teams.
									</p>
								</div>

								{setAccountRoleMutation.isError && (
									<div className="rounded-md bg-destructive/10 p-3 text-sm text-red-400">
										{setAccountRoleMutation.error.message}
									</div>
								)}

								<DialogFooter>
									<Button type="button" variant="outline" onClick={onClose}>
										Cancel
									</Button>
									<Button
										type="submit"
										disabled={!hasAccountRoleChanges || isSaving}
									>
										{setAccountRoleMutation.isPending ? "Saving..." : "Save Account Role"}
									</Button>
								</DialogFooter>
							</form>
						</TabsContent>

						{/* Teams Tab */}
						<TabsContent value="teams" className="mt-4">
							<form onSubmit={(e) => { e.preventDefault(); void handleSaveTeamChanges(); }} className="space-y-4">
								{/* Add team button */}
								{availableTeams.length > 0 && (
									<AddTeamPopover
										availableTeams={availableTeams}
										roles={rolesQuery.data ?? []}
										onAdd={handleAddTeam}
									/>
								)}

								{/* Team memberships table */}
								{effectiveTeams.length === 0 ? (
									<div className="text-center py-8 text-muted-foreground">
										<p>No team memberships</p>
										<p className="text-sm">Add this user to teams to grant team-specific access</p>
									</div>
								) : (
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>Team</TableHead>
												<TableHead>Role</TableHead>
												<TableHead className="w-[60px]">Actions</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{effectiveTeams.map((team) => (
												<TableRow key={team.id}>
													<TableCell>
														{team.name}
														{team.isNew && (
															<Badge variant="outline" className="ml-2 text-xs">New</Badge>
														)}
													</TableCell>
													<TableCell>
														<TeamRoleSelector
															currentRoleId={team.roleId}
															currentRoleName={team.roleName}
															roles={rolesQuery.data ?? []}
															onChange={(roleId) => { handleUpdateTeamRole(team.id, roleId); }}
														/>
													</TableCell>
													<TableCell>
														<Button
															variant="ghost"
															size="icon"
															onClick={() => { handleRemoveTeam(team.id); }}
														>
															<Trash2 className="h-4 w-4 text-red-400" />
														</Button>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								)}

								{/* Warning about removing all teams */}
								{effectiveTeams.length === 0 && hasTeamChanges && (
									<div className="rounded-md bg-white/10 border border-white/20 p-3 text-sm text-white/70">
										Removing all teams will remove team-specific access. The user will retain account-level access based on their account role.
									</div>
								)}

								<DialogFooter>
									<Button type="button" variant="outline" onClick={onClose}>
										Cancel
									</Button>
									<Button
										type="submit"
										disabled={!hasTeamChanges || isSaving}
									>
										{isSaving ? "Saving..." : "Save Team Changes"}
									</Button>
								</DialogFooter>
							</form>
						</TabsContent>
					</Tabs>
				)}
			</DialogContent>
		</Dialog>
	);
}

/**
 * Popover for adding a user to a team
 * @param root0 - Component props
 * @param root0.availableTeams - Teams the user can be added to
 * @param root0.roles - Available roles for team membership
 * @param root0.onAdd - Callback when a team is added
 */
function AddTeamPopover({
	availableTeams,
	roles,
	onAdd,
}: {
	availableTeams: { id: string; name: string }[];
	roles: { id: string; name: string; slug: string; description: string | null }[];
	onAdd: (teamId: string, roleId: string) => void;
}) {
	const [open, setOpen] = useState(false);
	const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
	const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

	// Auto-select viewer role
	useEffect(() => {
		if (!selectedRoleId && roles.length > 0) {
			const viewerRole = roles.find((r) => r.slug === "viewer");
			setSelectedRoleId(viewerRole?.id ?? roles[0]?.id ?? null);
		}
	}, [roles, selectedRoleId]);

	/**
	 * Handles adding the selected team with role
	 */
	const handleAdd = () => {
		if (selectedTeamId && selectedRoleId) {
			onAdd(selectedTeamId, selectedRoleId);
			setSelectedTeamId(null);
			setOpen(false);
		}
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button variant="outline" className="w-full">
					<Plus className="mr-2 h-4 w-4" />
					Add to Team
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[400px] p-4" align="start">
				<div className="space-y-4">
					<div className="space-y-2">
						<Label>Team</Label>
						<Command className="border rounded-md">
							<CommandInput placeholder="Search teams..." />
							<CommandList>
								<CommandEmpty>No teams available.</CommandEmpty>
								<CommandGroup>
									{availableTeams.map((team) => (
										<CommandItem
											key={team.id}
											onSelect={() => { setSelectedTeamId(team.id); }}
										>
											<Check
												className={cn(
													"mr-2 h-4 w-4",
													selectedTeamId === team.id ? "opacity-100" : "opacity-0"
												)}
											/>
											{team.name}
										</CommandItem>
									))}
								</CommandGroup>
							</CommandList>
						</Command>
					</div>

					<div className="space-y-2">
						<Label>Role in Team</Label>
						<Command className="border rounded-md">
							<CommandList>
								<CommandGroup>
									{roles.map((role) => (
										<CommandItem
											key={role.id}
											onSelect={() => { setSelectedRoleId(role.id); }}
										>
											<Check
												className={cn(
													"mr-2 h-4 w-4",
													selectedRoleId === role.id ? "opacity-100" : "opacity-0"
												)}
											/>
											<div className="flex flex-col">
												<span>{role.name}</span>
												{role.description && (
													<span className="text-xs text-muted-foreground">
														{role.description}
													</span>
												)}
											</div>
										</CommandItem>
									))}
								</CommandGroup>
							</CommandList>
						</Command>
					</div>

					<Button
						className="w-full"
						onClick={handleAdd}
						disabled={!selectedTeamId || !selectedRoleId}
					>
						Add to Team
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	);
}

/**
 * Inline role selector for team memberships
 * @param root0 - Component props
 * @param root0.currentRoleId - The currently selected role ID
 * @param root0.currentRoleName - The currently selected role name
 * @param root0.roles - Available roles to choose from
 * @param root0.onChange - Callback when role selection changes
 */
function TeamRoleSelector({
	currentRoleId,
	currentRoleName,
	roles,
	onChange,
}: {
	currentRoleId: string;
	currentRoleName: string;
	roles: { id: string; name: string; description: string | null }[];
	onChange: (roleId: string) => void;
}) {
	const [open, setOpen] = useState(false);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button variant="ghost" size="sm" className="h-8 justify-between">
					{currentRoleName}
					<ChevronDown className="ml-2 h-3 w-3 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[200px] p-0" align="start">
				<Command>
					<CommandList>
						<CommandGroup>
							{roles.map((role) => (
								<CommandItem
									key={role.id}
									onSelect={() => {
										onChange(role.id);
										setOpen(false);
									}}
								>
									<Check
										className={cn(
											"mr-2 h-4 w-4",
											currentRoleId === role.id ? "opacity-100" : "opacity-0"
										)}
									/>
									{role.name}
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
